<script setup lang="ts">
import type { ApplicationAcceptanceUpdate } from "@profile-service-types/application-acceptance-update";
import { HostAcceptedStatus } from "@profile-service-types/host-accepted-status";
import Dropdown from "primevue/dropdown";
import { computed, defineProps, type PropType, ref } from "vue";
import ApiTextArea from "@/components/forms/SharedComponents/ApiTextArea.vue";
import Modal from "@/components/modals/Modal.vue";
import BaseButton from "@/components/shared/Button/BaseButton.vue";
import CheckIcon from "@/components/shared/icons/CheckIcon.vue";
import CloseIconLarge from "@/components/shared/icons/CloseIconLarge.vue";
import PublishedWithChangesIcon from "@/components/shared/icons/PublishedWithChangesIcon.vue";
import TvOptionsEditChannelsIcon from "@/components/shared/icons/TvOptionsEditChannelsIcon.vue";
import UnpublishedIcon from "@/components/shared/icons/UnpublishedIcon.vue";
import UserAttributesIcon from "@/components/shared/icons/UserAttributesIcon.vue";
import { useApplicationMasterRecord } from "@/composables/useApplicationMasterRecord";
import { MAX_NOTE_TEXTAREA_LIMIT } from "@/constants";
import profileService from "@/services/profile.js";
import { parseAndFormatISODateStringToDate } from "@/util/date";
import { type AlgoliaStudentOrder, DEFAULT_STUDENT_ORDER } from "../types/algolia_student_orders";
import { ACCEPTANCE_STATUS, HOST_ACCEPTED_CONDITION } from "./utils";

export interface AlgoliaStudentOrderExt extends AlgoliaStudentOrder {
  application?: {
    id: string;
    host_accepted_status: string;
  };
}

export interface SubmittedResponse {
  success: boolean;
  error?: Error | string;
  data?: ApplicationUpdate;
}

interface ApplicationUpdate {
  applicationId: string;
  payload: ApplicationAcceptanceUpdate;
}

interface Emits {
  (event: "closeModal"): void;
  (event: "submitted", value: SubmittedResponse): void;
}

interface TimelineEvent {
  date?: string;
  actionTakenBy?: string;
  reason: string | null;
  label: string;
}

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  isViewMode: {
    type: Boolean,
    default: false,
  },
  maxLength: {
    type: Number,
    default: MAX_NOTE_TEXTAREA_LIMIT,
  },
  data: {
    type: Object as PropType<AlgoliaStudentOrderExt>,
    required: true,
    default: DEFAULT_STUDENT_ORDER,
  },
});

const emit = defineEmits<Emits>();
const dataSubmitted = ref<boolean>(false);
const hostAcceptedCondition = ref<HostAcceptedStatus>();
const reason = ref<string>();
const requiresAdditionalMaterials = ref<boolean>(true);
const applicationId = computed<string>(() => props.data.order?.core?.application_id ?? "");
const status = computed<HostAcceptedStatus | undefined>(() => {
  for (const key in HostAcceptedStatus) {
    if (HostAcceptedStatus[key as keyof typeof HostAcceptedStatus] === props.data.application?.host_accepted_status) {
      return HostAcceptedStatus[key as keyof typeof HostAcceptedStatus];
    }
  }
  return undefined;
});
const isReasonVisible = computed<boolean>(() => {
  if (status.value === ACCEPTANCE_STATUS.accepted) return false;
  if (
    status.value === ACCEPTANCE_STATUS.conditionallyAccepted &&
    hostAcceptedCondition.value !== HOST_ACCEPTED_CONDITION.other
  )
    return false;
  return true;
});
const isProvisionallyAccepted = computed<boolean>(() => {
  return props.data.application?.host_accepted_status === ACCEPTANCE_STATUS.provisionallyAccepted;
});

const experience = computed<string>(() => props.data.order?.session?.name ?? "");
const studentName = computed<string>(
  () =>
    `${props.data.student?.first_name_legal ?? props.data.student?.first_name_preferred} ${
      props.data.student?.last_name
    }`,
);
const { application } = useApplicationMasterRecord({ applicationId: applicationId.value });
const actionLabelStatused = computed<string | undefined>(() => {
  if (status.value === ACCEPTANCE_STATUS.accepted) return "Accept For";
  if (status.value === ACCEPTANCE_STATUS.notAccepted) return "Not Accept For";
  if (status.value === ACCEPTANCE_STATUS.provisionallyAccepted) return "Provisionally Accept For";
  if (status.value === ACCEPTANCE_STATUS.conditionallyAccepted) return "Conditionally Accept For";
  return undefined;
});
const getAcceptanceHeaderLabel = (status: HostAcceptedStatus): string | null => {
  switch (status) {
    case ACCEPTANCE_STATUS.accepted:
      return "ACCEPT EXPERIENCE";
    case ACCEPTANCE_STATUS.notAccepted:
      return "NOT ACCEPT";
    case ACCEPTANCE_STATUS.provisionallyAccepted:
      return "PROVISIONALLY ACCEPT EXPERIENCE";
    case ACCEPTANCE_STATUS.conditionallyAccepted:
      return "CONDITIONALLY ACCEPT EXPERIENCE";
    default:
      return null;
  }
};
const acceptanceTimeline = computed<TimelineEvent[]>(() => {
  const events = [
    {
      date: application.value?.host_accepted_at,
      actionTakenBy: application.value?.accepted_by,
      reason: null,
      label: "Accepted Date",
    },
    {
      date: application.value?.host_rejected_at,
      actionTakenBy: application.value?.rejected_by,
      reason: application.value?.host_rejected_reason,
      label: "Rejected Date",
    },
    {
      date: application.value?.host_provisionally_accepted_at,
      actionTakenBy: application.value?.provisionally_accepted_by,
      reason: application.value?.host_accepted_provisionally_reason,
      label: "Provisionally Accepted Date",
    },
    {
      date: application.value?.host_conditionally_accepted_at,
      actionTakenBy: application.value?.conditionally_accepted_by,
      reason: application.value?.host_accepted_condition_other_reason ?? application.value?.host_accepted_condition,
      label: "Conditionally Accepted Date",
    },
  ];

  return events
    .filter((event) => !!event.date)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
});

const onCancel = (): void => {
  emit("closeModal");
  requiresAdditionalMaterials.value = true;
  dataSubmitted.value = false;
};

const onSubmit = async (): Promise<void> => {
  dataSubmitted.value = true;
  let additionalFields = {};
  if (status.value === ACCEPTANCE_STATUS.notAccepted) {
    additionalFields = {
      rejected_reason: reason.value,
    };
  }
  if (status.value === ACCEPTANCE_STATUS.conditionallyAccepted) {
    additionalFields = {
      accepted_condition: hostAcceptedCondition.value,
      accepted_condition_other_reason: reason.value,
    };
  }
  if (status.value === ACCEPTANCE_STATUS.provisionallyAccepted) {
    additionalFields = {
      accepted_provisionally_reason: reason.value,
      requires_additional_application_materials: requiresAdditionalMaterials.value,
    };
  }
  const data: ApplicationUpdate = {
    applicationId: applicationId.value,
    payload: {
      status: status.value ?? HostAcceptedStatus.NotAccepted, // Need a default unknown value here
      ...additionalFields,
    },
  };

  try {
    await profileService.updateAcceptanceApplication(data);
    emit("submitted", { success: true, data });
    emit("closeModal");
  } catch (error: unknown) {
    if (error instanceof Error) {
      emit("submitted", { success: false, error: error });
    } else {
      emit("submitted", {
        success: false,
        error: `An error occurred while updating application: ${applicationId.value}`,
      });
    }
  } finally {
    requiresAdditionalMaterials.value = true;
    dataSubmitted.value = false;
  }
};

const onFieldChange = (event) => {
  reason.value = event;
};

const isSubmitDisabled = computed<boolean>(() => {
  const hasReasonFilled = reason.value?.length;
  if (status.value === ACCEPTANCE_STATUS.accepted) return false;
  if (status.value === ACCEPTANCE_STATUS.notAccepted && !hasReasonFilled) {
    return true;
  }
  if (
    status.value === ACCEPTANCE_STATUS.conditionallyAccepted &&
    (!hostAcceptedCondition.value ||
      (hostAcceptedCondition.value === HOST_ACCEPTED_CONDITION.other && !hasReasonFilled))
  ) {
    return true;
  }
  return false;
});

const closeCancelButtonLabel = (): string => {
  return props.isViewMode ? "CLOSE" : "CANCEL";
};

const modalHeaderTitle = computed<string>(() => {
  if (props.isViewMode) return "VIEW DETAILS";
  if (status.value) {
    return getAcceptanceHeaderLabel(status.value) ?? "";
  }
  return "";
});
</script>

<template>
  <Modal
    data-testid="my-students-acceptance-modal"
    :open="props?.open"
    :show-logo="false"
    :close-on-outside-click="false"
    :rounded="false"
    :show-close-button="false"
    content-padding-class="p-5 sm:px-16 md:px-10 sm:py-16 sm:pb-8"
  >
    <template #modalHeader>
      <div
        class="flex flex-row justify-between h-[50px] bg-teal-accent items-center absolute inset-0 px-5 py-3 font-montserrat"
      >
        <div class="flex flex-row">
          <UserAttributesIcon v-if="props.isViewMode" />
          <CheckIcon v-if="!props.isViewMode && status === ACCEPTANCE_STATUS.accepted" fill="#1E264C" />
          <PublishedWithChangesIcon
            v-if="!props.isViewMode && status === ACCEPTANCE_STATUS.provisionallyAccepted"
            fill="#1E264C"
          />
          <UnpublishedIcon v-if="!props.isViewMode && status === ACCEPTANCE_STATUS.notAccepted" fill="#1E264C" />
          <TvOptionsEditChannelsIcon v-if="!props.isViewMode && status === ACCEPTANCE_STATUS.conditionallyAccepted" />
          <span class="text-indigo-base font-bold ml-1 text-sm">{{ modalHeaderTitle }}</span>
        </div>
        <button class="h-6 w-6" data-testid="close-modal-button" @click="onCancel">
          <span class="sr-only">Close</span>
          <CloseIconLarge />
        </button>
      </div>
    </template>
    <template #modal-content>
      <!-- View Mode -->
      <div
        v-if="props.isViewMode"
        class="flex flex-col mt-8 space-y-6 text-indigo-base min-h-1000px font-montserrat text-sm"
      >
        <div class="flex">
          <label class="font-semibold">
            Student Name
          </label>
          <label class="ml-2">
            {{ studentName }}
          </label>
        </div>
        <div class="flex flex-col">
          <label class="font-semibold">
            Experience
          </label>
          <label>
            {{ experience }}
          </label>
        </div>
        <div v-if="status" class="flex flex-col">
          <label class="font-semibold">
            Status
          </label>
          <label>
            {{ status }}
          </label>
        </div>
        <div v-for="(timeline, index) in acceptanceTimeline" :key="index" class="flex flex-col">
          <div v-if="timeline.date" class="flex">
            <div class="flex flex-col w-[50%]">
              <label class="font-semibold">
                {{ timeline.label }}
              </label>
              <label>
                {{ parseAndFormatISODateStringToDate(timeline.date, "MM/dd/yyyy") }}
              </label>
            </div>
            <div class="flex flex-col w-[50%]">
              <label class="font-semibold">
                Action Taken By
              </label>
              <label>
                {{ timeline.actionTakenBy }}
              </label>
            </div>
          </div>
          <div v-if="timeline.reason" class="flex flex-col mt-8">
            <label class="font-semibold">
              Reason
            </label>
            <label>
              {{ timeline.reason }}
            </label>
          </div>
        </div>
      </div>
      <!-- Edit Mode -->
      <div v-else class="flex flex-col mt-8 space-y-6 text-indigo-base min-h-1000px font-montserrat text-sm">
        <div v-if="actionLabelStatused" class="flex">
          <label class="font-semibold">
            {{ actionLabelStatused }}
          </label>
          <label class="ml-2">
            {{ studentName }}
          </label>
        </div>
        <div class="flex flex-col">
          <label class="font-semibold">
            Experience
          </label>
          <label>
            {{ experience }}
          </label>
        </div>
        <div v-if="status === ACCEPTANCE_STATUS.conditionallyAccepted" class="flex flex-col">
          <label class="font-semibold">
            Conditional Acceptance Terms
          </label>
          <Dropdown
            v-model="hostAcceptedCondition"
            class="flex-1 w-full border-gray-500 !outline-none"
            data-testid="my-students-host-accepted-condition"
            :pt="{
              wrapper: { style: 'overflow-y: scroll !important;' },
            }"
            :options="Object.values(HOST_ACCEPTED_CONDITION)"
            :append-to="`self`"
            :panel-class="['bg-white']"
          />
        </div>
        <div v-if="isReasonVisible">
          <label class="font-semibold">
            Reason
          </label>
          <ApiTextArea
            ref="modal-text-area"
            data-testid="my-students-acceptance-modal-reason-text-field"
            name="modal-text-area"
            :max-length="props.maxLength"
            custom-error-class="text-sm"
            label-span-custom-class="text-lg font-medium"
            @text-update="onFieldChange($event)"
          />
          <input
            v-if="isProvisionallyAccepted"
            type="checkbox"
            v-model="requiresAdditionalMaterials"
            :data-testid="`checkbox`"
            class="w-[16px] h-[16px] border-university-primary m-[8px] form-checkbox"
          />
          <label v-if="isProvisionallyAccepted">Requires additional application materials</label>
        </div>
      </div>
    </template>
    <template #modal-footer>
      <div class="flex align-center justify-center h-10 flex-0 text-sm">
        <BaseButton
          class="px-7 min-w-[140px]"
          :rounded="false"
          :outlined="true"
          data-test-id="my-students-modal-cancel-close-button"
          @click="onCancel"
          >{{ closeCancelButtonLabel() }}</BaseButton
        >
        <BaseButton
          v-if="!props.isViewMode"
          class="min-w-[140px]"
          :rounded="false"
          :filled="true"
          :disabled="isSubmitDisabled || dataSubmitted"
          data-test-id="my-students-modal-submit-button"
          @click="onSubmit"
          >SUBMIT</BaseButton
        >
      </div>
    </template>
  </Modal>
</template>
